import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      features: 'Features',
      pricing: 'Pricing',
      faq: 'FAQ',
      login: 'Login',
      getStarted: 'Get Started',
      dashboard: 'Dashboard',
      logout: 'Logout'
    },
    // Hero
    hero: {
      title: 'Viral Ad Content for Bulgarian Businesses',
      subtitle: 'AI-powered UGC hooks, scripts, and storyboards — built for local brands and small agencies that can\'t afford $10k/month ad shops.',
      cta: 'Start Creating Ads',
      secondary: 'See How It Works',
      previewLabel: 'Ad Generator Preview',
      poweredBy: 'Powered by',
      statsLine: 'ads generated by',
      statsBetaUsers: 'beta users'
    },
    testimonial: {
      kicker: 'What real users say',
      items: [
        {
          quote: 'AIgnyx saved me hours of ad-copy work on a real client project — the results were exactly in the tone and style I was looking for.',
          name: 'Вяра Стефчева',
          title: 'Entrepreneur & Lecturer @ SoftUni',
          initials: 'ВС',
          linkedin: 'https://www.linkedin.com/in/vyara-stefcheva',
        },
        {
          quote: "A wonderful app. It gave me advertising ideas for my salon I hadn't thought of. It's going to save me time and money and make it easier to grow my business.",
          name: 'Петя Ценьова',
          title: 'Owner, Kiara Hair Salon',
          initials: 'ПЦ',
          facebook: 'https://www.facebook.com/share/19MXmCuqzx/',
        },
        {
          quote: "A wonderful app, I'm very pleased — it's fast and easy to work with, and the great thing is it works in Bulgarian, which is a huge relief for me. I have a beauty studio, and I'm sure it will save me a lot of the hours I spend on my phone putting together one great ad.",
          name: 'София Хашалова',
          title: 'Owner, Bellissimo Beauty Salon',
          initials: 'СХ',
          facebook: 'https://www.facebook.com/share/197GeNKhib/',
        },
      ],
    },
    beforeAfter: {
      kicker: 'Before → After',
      title: 'One product description. A full ad in minutes.',
      subtitle: 'See how a single sentence turns into hooks, a script, a storyboard, and a video-ready output — the full AIgnyx pipeline end to end.',
      step1Label: 'Before',
      step1Desc: 'What you start with',
      step1Kicker: 'Product description',
      step2Label: 'AIgnyx generates',
      step2Desc: 'Hooks + UGC script',
      step2HooksTitle: 'Viral hooks',
      step2ScriptTitle: 'UGC script snippet',
      step3Label: 'Storyboard scene',
      step3Desc: 'Shot-by-shot direction',
      step3SceneLabel: 'Scene 2 · The Reveal',
      step4Label: 'After',
      step4Desc: 'Video-ready output',
      step4Caption: 'AI video in ~90 seconds via Kling',
      step4Cta: 'Open in Kling',
      step4VideoLabel: 'Real Kling AI output for this soap product',
      videoTapToPlay: 'Tap to play',
      hookTypes: { curiosity: 'Curiosity', problem_solution: 'Problem-Solution' }
    },
    ctaSection: {
      title: 'Ready to Create Ads with Viral Potential?',
      subtitle: 'Join thousands of e-commerce brands using AIgnyx to create high-converting UGC ads.'
    },
    // Features
    features: {
      title: 'Everything You Need to Create Ads with Viral Potential',
      subtitle: 'Powerful AI tools designed for e-commerce brands and creators',
      hookGen: {
        title: 'Viral-Potential Hook Generator',
        desc: 'Generate attention-grabbing hooks optimized for TikTok, Instagram, and YouTube Shorts.'
      },
      scriptGen: {
        title: 'UGC Script Generator',
        desc: 'Create complete short-form video scripts with intro hooks, demos, and CTAs.'
      },
      storyboard: {
        title: 'Storyboard Generator',
        desc: 'Visual scene-by-scene storyboards with camera angles and timing.'
      },
      variations: {
        title: 'Ad Variation Generator',
        desc: 'Generate multiple ad versions with different hooks, tones, and CTAs.'
      },
      competitor: {
        title: 'Competitor Analyzer',
        desc: 'Analyze competitor strategies and generate counter-positioning hooks.'
      },
      creator: {
        title: 'Creator Script Mode',
        desc: 'Scripts designed for UGC creators with dialogue and camera instructions.'
      },
      predictor: {
        title: 'Performance Predictor',
        desc: 'AI-powered prediction scores for hook strength and message clarity.'
      },
      multilang: {
        title: 'Multi-Language Support',
        desc: 'Generate ads in English, Spanish, German, and Bulgarian.'
      }
    },
    // How It Works
    howItWorks: {
      title: 'How It Works',
      subtitle: 'Create ads with viral potential in three simple steps',
      step1: {
        title: 'Enter Product Details',
        desc: 'Add your product URL, name, description, and target audience.'
      },
      step2: {
        title: 'Choose Your Format',
        desc: 'Select hooks, scripts, storyboards, or full ad packages.'
      },
      step3: {
        title: 'Generate & Export',
        desc: 'Get AI-generated content ready for your ads in seconds.'
      }
    },
    // Pricing
    pricing: {
      title: 'Simple, Transparent Pricing',
      subtitle: 'Choose the plan that fits your needs',
      monthly: 'Monthly',
      starter: {
        name: 'Starter',
        price: '$29',
        period: '/month',
        features: ['10 ads per month', 'All generators', 'Email support', 'Export options']
      },
      pro: {
        name: 'Pro',
        price: '$59',
        period: '/month',
        popular: 'Most Popular',
        features: ['50 ads per month', 'All generators', 'Priority support', 'Competitor analysis', 'Creator scripts']
      },
      agency: {
        name: 'Agency',
        price: '$129',
        period: '/month',
        features: ['Unlimited ads', 'All features', 'Priority support', 'API access', 'Team collaboration']
      },
      cta: 'Get Started'
    },
    // FAQ
    faq: {
      title: 'Frequently Asked Questions',
      q1: {
        q: 'What platforms does AIgnyx support?',
        a: 'AIgnyx generates content optimized for TikTok, Instagram Reels, and YouTube Shorts.'
      },
      q2: {
        q: 'Can I use AIgnyx for Shopify products?',
        a: 'Yes! Simply paste your Shopify product URL and we\'ll extract the details automatically.'
      },
      q3: {
        q: 'What languages are supported?',
        a: 'We support English, Spanish, German, and Bulgarian for all generated content.'
      },
      q4: {
        q: 'How accurate is the Performance Predictor?',
        a: 'Our AI analyzes hook strength, emotional triggers, and message clarity to provide actionable insights.'
      },
      q5: {
        q: 'Can I cancel my subscription anytime?',
        a: 'Yes, you can cancel your subscription at any time with no hidden fees.'
      }
    },
    // Dashboard
    dashboard: {
      title: 'Dashboard',
      newProject: 'New Project',
      projects: 'Projects',
      settings: 'Settings',
      billing: 'Billing',
      adsUsed: 'Ads Used',
      plan: 'Plan',
      noProjects: 'No projects yet. Create your first ad!',
      recentProjects: 'Recent Projects'
    },
    // Generator
    generator: {
      title: 'Ad Generator',
      productUrl: 'Product URL (optional)',
      productName: 'Product Name',
      productDesc: 'Product Description',
      targetAudience: 'Target Audience',
      keyBenefit: 'Key Benefit',
      platform: 'Platform',
      selectPlatform: 'Select platform',
      generate: 'Generate',
      generating: 'Generating',
      hooks: 'Hooks',
      scripts: 'Scripts',
      storyboard: 'Storyboard',
      variations: 'Variations',
      competitor: 'Competitor Analysis',
      creatorScript: 'Creator Script',
      predictor: 'Performance Predictor',
      copy: 'Copy',
      copied: 'Copied!',
      export: 'Export',
      save: 'Save to Project'
    },
    // Auth
    auth: {
      login: 'Login',
      signup: 'Sign Up',
      email: 'Email',
      password: 'Password',
      name: 'Full Name',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      orContinue: 'Or continue with',
      google: 'Google'
    },
    // Footer
    footer: {
      tagline: 'Generate UGC ads with viral potential using AI.',
      product: 'Product',
      company: 'Company',
      legal: 'Contact & Legal',
      features: 'Features',
      pricing: 'Pricing',
      about: 'About',
      contact: 'Contact',
      privacy: 'Privacy',
      terms: 'Terms',
      cookies: 'Cookies',
      copyright: '© 2026 AIgnyx. All rights reserved.'
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'Something went wrong',
      tryAgain: 'Try Again',
      back: 'Back',
      next: 'Next',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      view: 'View',
      download: 'Download'
    }
  },
  es: {
    nav: {
      features: 'Características',
      pricing: 'Precios',
      faq: 'FAQ',
      login: 'Iniciar Sesión',
      getStarted: 'Comenzar',
      dashboard: 'Panel',
      logout: 'Cerrar Sesión'
    },
    hero: {
      title: 'Contenido publicitario viral para negocios búlgaros',
      subtitle: 'Hooks, guiones y storyboards UGC generados por IA — creados para marcas locales y pequeñas agencias sin presupuestos de $10 000 al mes.',
      cta: 'Crear Anuncios',
      secondary: 'Ver Cómo Funciona',
      previewLabel: 'Vista previa del generador',
      poweredBy: 'Impulsado por',
      statsLine: 'anuncios generados por',
      statsBetaUsers: 'usuarios beta'
    },
    testimonial: {
      kicker: 'Lo que dicen los usuarios reales',
      items: [
        {
          quote: 'AIgnyx me ahorró horas de trabajo en textos publicitarios para un proyecto real de cliente — los resultados estaban exactamente en el tono y estilo que buscaba.',
          name: 'Вяра Стефчева',
          title: 'Emprendedora y profesora @ SoftUni',
          initials: 'ВС',
          linkedin: 'https://www.linkedin.com/in/vyara-stefcheva',
        },
        {
          quote: 'Una aplicación maravillosa. Me dio ideas publicitarias para mi salón que no se me habían ocurrido. Me va a ahorrar tiempo y dinero, y facilitará el crecimiento de mi negocio.',
          name: 'Петя Ценьова',
          title: 'Propietaria, Peluquería Kiara',
          initials: 'ПЦ',
          facebook: 'https://www.facebook.com/share/19MXmCuqzx/',
        },
        {
          quote: 'Una aplicación maravillosa, estoy muy satisfecha — es rápida y fácil de usar, y lo mejor es que funciona en búlgaro, lo cual es un gran alivio para mí. Tengo un estudio de belleza y estoy segura de que me ahorrará muchas de las horas que paso frente al teléfono creando un buen anuncio.',
          name: 'София Хашалова',
          title: 'Propietaria, Salón de Belleza Bellissimo',
          initials: 'СХ',
          facebook: 'https://www.facebook.com/share/197GeNKhib/',
        },
      ],
    },
    beforeAfter: {
      kicker: 'Antes → Después',
      title: 'Una descripción de producto. Un anuncio completo en minutos.',
      subtitle: 'Mira cómo una sola frase se convierte en hooks, un guión, un storyboard y un output listo para vídeo — todo el pipeline de AIgnyx de principio a fin.',
      step1Label: 'Antes',
      step1Desc: 'Con lo que empiezas',
      step1Kicker: 'Descripción del producto',
      step2Label: 'AIgnyx genera',
      step2Desc: 'Hooks + guión UGC',
      step2HooksTitle: 'Hooks virales',
      step2ScriptTitle: 'Fragmento de guión UGC',
      step3Label: 'Escena del storyboard',
      step3Desc: 'Dirección plano a plano',
      step3SceneLabel: 'Escena 2 · La revelación',
      step4Label: 'Después',
      step4Desc: 'Output listo para vídeo',
      step4Caption: 'Vídeo IA en ~90 segundos vía Kling',
      step4Cta: 'Abrir en Kling',
      step4VideoLabel: 'Salida real de Kling AI para este jabón',
      videoTapToPlay: 'Toca para reproducir',
      hookTypes: { curiosity: 'Curiosidad', problem_solution: 'Problema-Solución' }
    },
    ctaSection: {
      title: '¿Listo para Crear Anuncios con Potencial Viral?',
      subtitle: 'Únete a miles de marcas de e-commerce que usan AIgnyx para crear anuncios UGC de alto rendimiento.'
    },
    features: {
      title: 'Todo lo que Necesitas para Crear Anuncios con Potencial Viral',
      subtitle: 'Herramientas de IA para marcas de e-commerce y creadores',
      hookGen: {
        title: 'Generador de Hooks con Potencial Viral',
        desc: 'Genera hooks optimizados para TikTok, Instagram y YouTube Shorts.'
      },
      scriptGen: {
        title: 'Generador de Scripts UGC',
        desc: 'Crea scripts completos con hooks, demos y CTAs.'
      },
      storyboard: {
        title: 'Generador de Storyboard',
        desc: 'Storyboards visuales escena por escena con ángulos de cámara.'
      },
      variations: {
        title: 'Generador de Variaciones',
        desc: 'Genera múltiples versiones con diferentes hooks y CTAs.'
      },
      competitor: {
        title: 'Análisis de Competencia',
        desc: 'Analiza estrategias de competidores y genera contra-hooks.'
      },
      creator: {
        title: 'Modo Script Creador',
        desc: 'Scripts para creadores UGC con diálogos e instrucciones.'
      },
      predictor: {
        title: 'Predictor de Rendimiento',
        desc: 'Puntuaciones de IA para fuerza del hook y claridad del mensaje.'
      },
      multilang: {
        title: 'Soporte Multi-idioma',
        desc: 'Genera anuncios en inglés, español, alemán y búlgaro.'
      }
    },
    howItWorks: {
      title: 'Cómo Funciona',
      subtitle: 'Crea anuncios con potencial viral en tres pasos',
      step1: {
        title: 'Ingresa Detalles del Producto',
        desc: 'Agrega URL, nombre, descripción y audiencia objetivo.'
      },
      step2: {
        title: 'Elige Tu Formato',
        desc: 'Selecciona hooks, scripts, storyboards o paquetes completos.'
      },
      step3: {
        title: 'Genera y Exporta',
        desc: 'Obtén contenido generado por IA listo en segundos.'
      }
    },
    pricing: {
      title: 'Precios Simples y Transparentes',
      subtitle: 'Elige el plan que mejor se adapte',
      monthly: 'Mensual',
      starter: {
        name: 'Starter',
        price: '$29',
        period: '/mes',
        features: ['10 anuncios/mes', 'Todos los generadores', 'Soporte por email', 'Opciones de exportación']
      },
      pro: {
        name: 'Pro',
        price: '$59',
        period: '/mes',
        popular: 'Más Popular',
        features: ['50 anuncios/mes', 'Todos los generadores', 'Soporte prioritario', 'Análisis de competencia', 'Scripts para creadores']
      },
      agency: {
        name: 'Agencia',
        price: '$129',
        period: '/mes',
        features: ['Anuncios ilimitados', 'Todas las funciones', 'Soporte prioritario', 'Acceso API', 'Colaboración en equipo']
      },
      cta: 'Comenzar'
    },
    faq: {
      title: 'Preguntas Frecuentes',
      q1: { q: '¿Qué plataformas soporta AIgnyx?', a: 'AIgnyx genera contenido optimizado para TikTok, Instagram Reels y YouTube Shorts.' },
      q2: { q: '¿Puedo usar AIgnyx para productos de Shopify?', a: '¡Sí! Simplemente pega tu URL de Shopify y extraeremos los detalles automáticamente.' },
      q3: { q: '¿Qué idiomas están soportados?', a: 'Soportamos inglés, español, alemán y búlgaro.' },
      q4: { q: '¿Qué tan preciso es el Predictor de Rendimiento?', a: 'Nuestra IA analiza fuerza del hook, triggers emocionales y claridad del mensaje.' },
      q5: { q: '¿Puedo cancelar mi suscripción en cualquier momento?', a: 'Sí, puedes cancelar en cualquier momento sin cargos ocultos.' }
    },
    dashboard: {
      title: 'Panel',
      newProject: 'Nuevo Proyecto',
      projects: 'Proyectos',
      settings: 'Configuración',
      billing: 'Facturación',
      adsUsed: 'Anuncios Usados',
      plan: 'Plan',
      noProjects: 'Sin proyectos aún. ¡Crea tu primer anuncio!',
      recentProjects: 'Proyectos Recientes'
    },
    generator: {
      title: 'Generador de Anuncios',
      productUrl: 'URL del Producto (opcional)',
      productName: 'Nombre del Producto',
      productDesc: 'Descripción del Producto',
      targetAudience: 'Audiencia Objetivo',
      keyBenefit: 'Beneficio Principal',
      platform: 'Plataforma',
      selectPlatform: 'Seleccionar plataforma',
      generate: 'Generar',
      generating: 'Generando',
      hooks: 'Hooks',
      scripts: 'Scripts',
      storyboard: 'Storyboard',
      variations: 'Variaciones',
      competitor: 'Análisis de Competencia',
      creatorScript: 'Script Creador',
      predictor: 'Predictor de Rendimiento',
      copy: 'Copiar',
      copied: '¡Copiado!',
      export: 'Exportar',
      save: 'Guardar en Proyecto'
    },
    auth: {
      login: 'Iniciar Sesión',
      signup: 'Registrarse',
      email: 'Email',
      password: 'Contraseña',
      name: 'Nombre Completo',
      forgotPassword: '¿Olvidaste tu contraseña?',
      noAccount: '¿No tienes cuenta?',
      hasAccount: '¿Ya tienes cuenta?',
      orContinue: 'O continúa con',
      google: 'Google'
    },
    footer: {
      tagline: 'Genera anuncios UGC con potencial viral con IA.',
      product: 'Producto',
      company: 'Empresa',
      legal: 'Contacto y aviso legal',
      features: 'Características',
      pricing: 'Precios',
      about: 'Acerca de',
      contact: 'Contacto',
      privacy: 'Privacidad',
      terms: 'Términos',
      cookies: 'Cookies',
      copyright: '© 2026 AIgnyx. Todos los derechos reservados.'
    },
    common: {
      loading: 'Cargando...',
      error: 'Algo salió mal',
      tryAgain: 'Intentar de Nuevo',
      back: 'Atrás',
      next: 'Siguiente',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      view: 'Ver',
      download: 'Descargar'
    }
  },
  de: {
    nav: {
      features: 'Funktionen',
      pricing: 'Preise',
      faq: 'FAQ',
      login: 'Anmelden',
      getStarted: 'Loslegen',
      dashboard: 'Dashboard',
      logout: 'Abmelden'
    },
    hero: {
      title: 'Virale Werbeinhalte für bulgarische Unternehmen',
      subtitle: 'KI-generierte UGC-Hooks, Skripte und Storyboards — gebaut für lokale Marken und kleine Agenturen ohne 10.000-Dollar-Monatsbudgets.',
      cta: 'Anzeigen Erstellen',
      secondary: 'So Funktioniert Es',
      previewLabel: 'Generator-Vorschau',
      poweredBy: 'Angetrieben von',
      statsLine: 'Anzeigen erstellt von',
      statsBetaUsers: 'Beta-Nutzern'
    },
    testimonial: {
      kicker: 'Was echte Nutzer sagen',
      items: [
        {
          quote: 'AIgnyx hat mir Stunden an Werbetext-Arbeit für ein echtes Kundenprojekt erspart — die Ergebnisse trafen genau den Ton und Stil, den ich gesucht habe.',
          name: 'Вяра Стефчева',
          title: 'Unternehmerin & Dozentin @ SoftUni',
          initials: 'ВС',
          linkedin: 'https://www.linkedin.com/in/vyara-stefcheva',
        },
        {
          quote: 'Eine wunderbare App. Sie gab mir Werbeideen für meinen Salon, an die ich nicht gedacht hatte. Sie wird mir Zeit und Geld sparen und das Wachstum meines Geschäfts erleichtern.',
          name: 'Петя Ценьова',
          title: 'Inhaberin, Friseursalon Kiara',
          initials: 'ПЦ',
          facebook: 'https://www.facebook.com/share/19MXmCuqzx/',
        },
        {
          quote: 'Eine wunderbare App, ich bin sehr zufrieden — sie ist schnell und einfach zu bedienen, und das Beste ist, dass sie auf Bulgarisch funktioniert, was für mich eine große Erleichterung ist. Ich habe ein Beauty-Studio und bin sicher, dass sie mir viele der Stunden erspart, die ich vor dem Telefon verbringe, um eine großartige Anzeige zu erstellen.',
          name: 'София Хашалова',
          title: 'Inhaberin, Beautysalon Bellissimo',
          initials: 'СХ',
          facebook: 'https://www.facebook.com/share/197GeNKhib/',
        },
      ],
    },
    beforeAfter: {
      kicker: 'Vorher → Nachher',
      title: 'Eine Produktbeschreibung. Eine komplette Anzeige in Minuten.',
      subtitle: 'Sieh, wie aus einem einzigen Satz Hooks, ein Skript, ein Storyboard und ein videoreifer Output entstehen — die gesamte AIgnyx-Pipeline von Anfang bis Ende.',
      step1Label: 'Vorher',
      step1Desc: 'Womit du beginnst',
      step1Kicker: 'Produktbeschreibung',
      step2Label: 'AIgnyx generiert',
      step2Desc: 'Hooks + UGC-Skript',
      step2HooksTitle: 'Virale Hooks',
      step2ScriptTitle: 'UGC-Skript-Ausschnitt',
      step3Label: 'Storyboard-Szene',
      step3Desc: 'Szene für Szene Regie',
      step3SceneLabel: 'Szene 2 · Die Enthüllung',
      step4Label: 'Nachher',
      step4Desc: 'Videoreifer Output',
      step4Caption: 'KI-Video in ~90 Sekunden via Kling',
      step4Cta: 'In Kling öffnen',
      step4VideoLabel: 'Echtes Kling-AI-Ergebnis für diese Seife',
      videoTapToPlay: 'Zum Abspielen tippen',
      hookTypes: { curiosity: 'Neugier', problem_solution: 'Problem-Lösung' }
    },
    ctaSection: {
      title: 'Bereit für Anzeigen mit viralem Potenzial?',
      subtitle: 'Schließe dich Tausenden von E-Commerce-Marken an, die AIgnyx nutzen, um konversionsstarke UGC-Anzeigen zu erstellen.'
    },
    features: {
      title: 'Alles für Anzeigen mit viralem Potenzial',
      subtitle: 'Leistungsstarke KI-Tools für E-Commerce-Marken und Creator',
      hookGen: { title: 'Hook-Generator mit viralem Potenzial', desc: 'Erstelle aufmerksamkeitsstarke Hooks für TikTok, Instagram und YouTube.' },
      scriptGen: { title: 'UGC-Skript-Generator', desc: 'Erstelle komplette Kurzform-Video-Skripte mit Hooks und CTAs.' },
      storyboard: { title: 'Storyboard-Generator', desc: 'Visuelle Szene-für-Szene Storyboards mit Kamerawinkeln.' },
      variations: { title: 'Varianten-Generator', desc: 'Erstelle mehrere Anzeigenversionen mit verschiedenen Hooks.' },
      competitor: { title: 'Wettbewerber-Analyse', desc: 'Analysiere Wettbewerberstrategien und generiere Gegen-Hooks.' },
      creator: { title: 'Creator-Skript-Modus', desc: 'Skripte für UGC-Creator mit Dialog und Kameraanweisungen.' },
      predictor: { title: 'Leistungs-Prädiktor', desc: 'KI-gestützte Bewertungen für Hook-Stärke und Klarheit.' },
      multilang: { title: 'Mehrsprachig', desc: 'Erstelle Anzeigen in Englisch, Spanisch, Deutsch und Bulgarisch.' }
    },
    howItWorks: {
      title: 'So Funktioniert Es',
      subtitle: 'Erstelle Anzeigen mit viralem Potenzial in drei Schritten',
      step1: { title: 'Produktdetails Eingeben', desc: 'Füge URL, Name, Beschreibung und Zielgruppe hinzu.' },
      step2: { title: 'Format Wählen', desc: 'Wähle Hooks, Skripte, Storyboards oder komplette Pakete.' },
      step3: { title: 'Generieren & Exportieren', desc: 'Erhalte KI-generierte Inhalte in Sekunden.' }
    },
    pricing: {
      title: 'Einfache, Transparente Preise',
      subtitle: 'Wähle den passenden Plan',
      monthly: 'Monatlich',
      starter: { name: 'Starter', price: '29€', period: '/Monat', features: ['10 Anzeigen/Monat', 'Alle Generatoren', 'E-Mail-Support', 'Export-Optionen'] },
      pro: { name: 'Pro', price: '59€', period: '/Monat', popular: 'Beliebteste', features: ['50 Anzeigen/Monat', 'Alle Generatoren', 'Prioritäts-Support', 'Wettbewerber-Analyse', 'Creator-Skripte'] },
      agency: { name: 'Agentur', price: '129€', period: '/Monat', features: ['Unbegrenzte Anzeigen', 'Alle Funktionen', 'Prioritäts-Support', 'API-Zugang', 'Team-Zusammenarbeit'] },
      cta: 'Loslegen'
    },
    faq: {
      title: 'Häufig Gestellte Fragen',
      q1: { q: 'Welche Plattformen unterstützt AIgnyx?', a: 'AIgnyx erstellt Inhalte für TikTok, Instagram Reels und YouTube Shorts.' },
      q2: { q: 'Kann ich AIgnyx für Shopify-Produkte nutzen?', a: 'Ja! Füge einfach deine Shopify-URL ein.' },
      q3: { q: 'Welche Sprachen werden unterstützt?', a: 'Wir unterstützen Englisch, Spanisch, Deutsch und Bulgarisch.' },
      q4: { q: 'Wie genau ist der Leistungs-Prädiktor?', a: 'Unsere KI analysiert Hook-Stärke, emotionale Trigger und Klarheit.' },
      q5: { q: 'Kann ich jederzeit kündigen?', a: 'Ja, du kannst jederzeit ohne versteckte Gebühren kündigen.' }
    },
    dashboard: { title: 'Dashboard', newProject: 'Neues Projekt', projects: 'Projekte', settings: 'Einstellungen', billing: 'Abrechnung', adsUsed: 'Anzeigen Verwendet', plan: 'Plan', noProjects: 'Noch keine Projekte. Erstelle deine erste Anzeige!', recentProjects: 'Aktuelle Projekte' },
    generator: { title: 'Anzeigen-Generator', productUrl: 'Produkt-URL (optional)', productName: 'Produktname', productDesc: 'Produktbeschreibung', targetAudience: 'Zielgruppe', keyBenefit: 'Hauptvorteil', platform: 'Plattform', selectPlatform: 'Plattform wählen', generate: 'Generieren', generating: 'Generiere', hooks: 'Hooks', scripts: 'Skripte', storyboard: 'Storyboard', variations: 'Varianten', competitor: 'Wettbewerber-Analyse', creatorScript: 'Creator-Skript', predictor: 'Leistungs-Prädiktor', copy: 'Kopieren', copied: 'Kopiert!', export: 'Exportieren', save: 'Zum Projekt Speichern' },
    auth: { login: 'Anmelden', signup: 'Registrieren', email: 'E-Mail', password: 'Passwort', name: 'Vollständiger Name', forgotPassword: 'Passwort vergessen?', noAccount: 'Kein Konto?', hasAccount: 'Bereits ein Konto?', orContinue: 'Oder weiter mit', google: 'Google' },
    footer: { tagline: 'Erstelle UGC-Anzeigen mit viralem Potenzial mit KI.', product: 'Produkt', company: 'Unternehmen', legal: 'Kontakt & Recht', features: 'Funktionen', pricing: 'Preise', about: 'Über uns', contact: 'Kontakt', privacy: 'Datenschutz', terms: 'AGB', cookies: 'Cookies', copyright: '© 2026 AIgnyx. Alle Rechte vorbehalten.' },
    common: { loading: 'Laden...', error: 'Etwas ist schiefgelaufen', tryAgain: 'Erneut Versuchen', back: 'Zurück', next: 'Weiter', save: 'Speichern', cancel: 'Abbrechen', delete: 'Löschen', edit: 'Bearbeiten', view: 'Ansehen', download: 'Herunterladen' }
  },
  bg: {
    nav: {
      features: 'Функции',
      pricing: 'Цени',
      faq: 'ЧЗВ',
      login: 'Вход',
      getStarted: 'Започни',
      dashboard: 'Табло',
      logout: 'Изход'
    },
    hero: {
      title: 'Вирусно рекламно съдържание за българския бизнес',
      subtitle: 'AI-генерирани UGC hook-ове, скриптове и storyboard-и — създадени за локални марки и малки агенции без бюджет от $10 000 на месец.',
      cta: 'Създай Реклами',
      secondary: 'Виж Как Работи',
      previewLabel: 'Преглед на генератора',
      poweredBy: 'Задвижвано от',
      statsLine: 'реклами, генерирани от',
      statsBetaUsers: 'beta потребители'
    },
    testimonial: {
      kicker: 'Какво казват реалните потребители',
      items: [
        {
          quote: 'AIgnyx ми спести часове работа по рекламен текст за реален клиентски проект — резултатите бяха точно в тон и стил, който търсех.',
          name: 'Вяра Стефчева',
          title: 'Предприемач и лектор @ SoftUni',
          initials: 'ВС',
          linkedin: 'https://www.linkedin.com/in/vyara-stefcheva',
        },
        {
          quote: 'Чудесно приложение. Даде ми идеи за реклама на салона, за които не съм се сещала. Ще ми спести време и средства и ще улесни разширяването на дейността ми.',
          name: 'Петя Ценьова',
          title: 'Собственик, фризьорски салон „Киара"',
          initials: 'ПЦ',
          facebook: 'https://www.facebook.com/share/19MXmCuqzx/',
        },
        {
          quote: 'Прекрасно приложение, много съм доволна, бързо и лесно се работи с него, хубавото е че може на български език, което е голямо улеснение за мен. Имам студио за красота, и съм сигурна че ще ми спести голяма част от часовете прекарани пред телефона, правейки една прекрасна реклама.',
          name: 'София Хашалова',
          title: 'Собственик, Салон за красота „Белисимо"',
          initials: 'СХ',
          facebook: 'https://www.facebook.com/share/197GeNKhib/',
        },
      ],
    },
    beforeAfter: {
      kicker: 'Преди → След',
      title: 'Едно описание на продукта. Готова реклама за минути.',
      subtitle: 'Виж как едно изречение се превръща в hook-ове, скрипт, storyboard и готово видео — целият pipeline на AIgnyx от край до край.',
      step1Label: 'Преди',
      step1Desc: 'С какво започваш',
      step1Kicker: 'Описание на продукта',
      step2Label: 'AIgnyx генерира',
      step2Desc: 'Hook-ове + UGC скрипт',
      step2HooksTitle: 'Вирусни hook-ове',
      step2ScriptTitle: 'Откъс от UGC скрипт',
      step3Label: 'Storyboard кадър',
      step3Desc: 'Режисура кадър по кадър',
      step3SceneLabel: 'Кадър 2 · Разкриването',
      step4Label: 'След',
      step4Desc: 'Готово за видео',
      step4Caption: 'AI видео за ~90 секунди чрез Kling',
      step4Cta: 'Отвори в Kling',
      step4VideoLabel: 'Реалният Kling AI резултат за този сапун',
      videoTapToPlay: 'Докосни за пускане',
      hookTypes: { curiosity: 'Любопитство', problem_solution: 'Проблем-Решение' }
    },
    ctaSection: {
      title: 'Готов ли си да създаваш реклами с вирусен потенциал?',
      subtitle: 'Присъедини се към хилядите e-commerce брандове, които използват AIgnyx, за да създават високо конвертиращи UGC реклами.'
    },
    features: {
      title: 'Всичко за Реклами с Вирусен Потенциал',
      subtitle: 'Мощни AI инструменти за e-commerce брандове и създатели',
      hookGen: { title: 'Генератор на Hooks с Вирусен Потенциал', desc: 'Създай привличащи вниманието hooks за TikTok, Instagram и YouTube.' },
      scriptGen: { title: 'UGC Скрипт Генератор', desc: 'Създай пълни видео скриптове с hooks, демота и CTA.' },
      storyboard: { title: 'Storyboard Генератор', desc: 'Визуални сцена по сцена storyboards с ъгли на камера.' },
      variations: { title: 'Генератор на Вариации', desc: 'Генерирай множество версии с различни hooks и тонове.' },
      competitor: { title: 'Анализ на Конкуренти', desc: 'Анализирай стратегии на конкуренти и генерирай контра-hooks.' },
      creator: { title: 'Режим Създател Скрипт', desc: 'Скриптове за UGC създатели с диалог и инструкции.' },
      predictor: { title: 'Предиктор за Представяне', desc: 'AI оценки за сила на hook и яснота на посланието.' },
      multilang: { title: 'Многоезична Поддръжка', desc: 'Генерирай реклами на английски, испански, немски и български.' }
    },
    howItWorks: {
      title: 'Как Работи',
      subtitle: 'Създай реклами с вирусен потенциал в три стъпки',
      step1: { title: 'Въведи Детайли за Продукта', desc: 'Добави URL, име, описание и целева аудитория.' },
      step2: { title: 'Избери Формат', desc: 'Избери hooks, скриптове, storyboards или пълни пакети.' },
      step3: { title: 'Генерирай и Експортирай', desc: 'Получи AI-генерирано съдържание за секунди.' }
    },
    pricing: {
      title: 'Прости, Прозрачни Цени',
      subtitle: 'Избери плана, който ти подхожда',
      monthly: 'Месечно',
      starter: { name: 'Starter', price: '$29', period: '/месец', features: ['10 реклами/месец', 'Всички генератори', 'Email поддръжка', 'Опции за експорт'] },
      pro: { name: 'Pro', price: '$59', period: '/месец', popular: 'Най-популярен', features: ['50 реклами/месец', 'Всички генератори', 'Приоритетна поддръжка', 'Анализ на конкуренти', 'Скриптове за създатели'] },
      agency: { name: 'Агенция', price: '$129', period: '/месец', features: ['Неограничени реклами', 'Всички функции', 'Приоритетна поддръжка', 'API достъп', 'Екипно сътрудничество'] },
      cta: 'Започни'
    },
    faq: {
      title: 'Често Задавани Въпроси',
      q1: { q: 'Кои платформи поддържа AIgnyx?', a: 'AIgnyx генерира съдържание за TikTok, Instagram Reels и YouTube Shorts.' },
      q2: { q: 'Мога ли да използвам AIgnyx за Shopify продукти?', a: 'Да! Просто постави своя Shopify URL.' },
      q3: { q: 'Кои езици се поддържат?', a: 'Поддържаме английски, испански, немски и български.' },
      q4: { q: 'Колко точен е Предикторът за Представяне?', a: 'Нашият AI анализира сила на hook, емоционални тригери и яснота.' },
      q5: { q: 'Мога ли да анулирам абонамента си по всяко време?', a: 'Да, можеш да анулираш по всяко време без скрити такси.' }
    },
    dashboard: { title: 'Табло', newProject: 'Нов Проект', projects: 'Проекти', settings: 'Настройки', billing: 'Плащания', adsUsed: 'Използвани Реклами', plan: 'План', noProjects: 'Все още няма проекти. Създай първата си реклама!', recentProjects: 'Скорошни Проекти' },
    generator: { title: 'Генератор на Реклами', productUrl: 'URL на Продукта (по избор)', productName: 'Име на Продукта', productDesc: 'Описание на Продукта', targetAudience: 'Целева Аудитория', keyBenefit: 'Основна Полза', platform: 'Платформа', selectPlatform: 'Избери платформа', generate: 'Генерирай', generating: 'Генериране', hooks: 'Hooks', scripts: 'Скриптове', storyboard: 'Storyboard', variations: 'Вариации', competitor: 'Анализ на Конкуренти', creatorScript: 'Скрипт за Създатели', predictor: 'Предиктор за Представяне', copy: 'Копирай', copied: 'Копирано!', export: 'Експортирай', save: 'Запази в Проект' },
    auth: { login: 'Вход', signup: 'Регистрация', email: 'Имейл', password: 'Парола', name: 'Пълно Име', forgotPassword: 'Забравена парола?', noAccount: 'Нямаш акаунт?', hasAccount: 'Вече имаш акаунт?', orContinue: 'Или продължи с', google: 'Google' },
    footer: { tagline: 'Генерирай UGC реклами с вирусен потенциал с AI.', product: 'Продукт', company: 'Компания', legal: 'Контакт и правна информация', features: 'Функции', pricing: 'Цени', about: 'За нас', contact: 'Контакт', privacy: 'Поверителност', terms: 'Условия', cookies: 'Бисквитки', copyright: '© 2026 AIgnyx. Всички права запазени.' },
    common: { loading: 'Зареждане...', error: 'Нещо се обърка', tryAgain: 'Опитай Отново', back: 'Назад', next: 'Напред', save: 'Запази', cancel: 'Отказ', delete: 'Изтрий', edit: 'Редактирай', view: 'Виж', download: 'Изтегли' }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('adforge_language');
      if (saved && translations[saved]) return saved;
      const browserLang = navigator.language.split('-')[0];
      return translations[browserLang] ? browserLang : 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('adforge_language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const value = {
    language,
    setLanguage,
    t,
    languages: [
      { code: 'en', name: 'English', flag: '🇬🇧' },
      { code: 'es', name: 'Español', flag: '🇪🇸' },
      { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
      { code: 'bg', name: 'Български', flag: '🇧🇬' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
